import { useEffect, useState } from 'react'
import { Plus, X, Loader2 } from 'lucide-react'
import { stickerApi, type StickerDTO, type StickerPackDTO } from '../../services/api'
import { dialog } from '../../services/dialog'
import StickerView from './StickerView'

interface Props {
  onSelect: (sticker: StickerDTO) => void
}

/**
 * Telegram-style stickers tab. Shows the user's installed packs as a
 * scroll-stack, with a "+ Add pack" affordance at the top that takes
 * a `t.me/addstickers/<name>` URL or bare name. Click a sticker → the
 * parent fires the send via onSelect.
 *
 * Per-user packs and server-wide packs are unified in one list (the
 * server merges both at /api/stickers/packs); the management actions
 * (uninstall) operate on the right scope automatically.
 */
export default function StickerPicker({ onSelect }: Props) {
  const [packs, setPacks] = useState<StickerPackDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  const refresh = async () => {
    try {
      const r = await stickerApi.packs()
      setPacks(r.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])

  const handleAdd = async () => {
    if (!input.trim()) return
    setAdding(true)
    setError(null)
    try {
      await stickerApi.install(input.trim(), 'USER')
      setInput('')
      setShowAdd(false)
      await refresh()
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to install pack')
    } finally {
      setAdding(false)
    }
  }

  const handleUninstall = async (packId: string) => {
    if (!(await dialog.confirm({ message: 'Remove this sticker pack?', tone: 'destructive', confirmLabel: 'Remove' }))) return
    await stickerApi.uninstall(packId)
    await refresh()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Add pack toolbar */}
      <div className="px-3 pt-3 pb-2 shrink-0 border-b border-border">
        {showAdd ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1.5">
              <input
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
                placeholder="t.me/addstickers/<name> or pack name"
                className="flex-1 px-3 py-1.5 text-sm bg-muted rounded outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
              />
              <button
                onClick={handleAdd}
                disabled={adding || !input.trim()}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
              >
                {adding ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
              </button>
              <button
                onClick={() => { setShowAdd(false); setInput(''); setError(null) }}
                className="px-2 py-1.5 text-muted-foreground hover:text-foreground"
                aria-label="Cancel"
              >
                <X size={16} />
              </button>
            </div>
            {error && <div className="text-xs text-destructive">{error}</div>}
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-accent rounded transition"
          >
            <Plus size={14} /> Add sticker pack
          </button>
        )}
      </div>

      {/* Pack stack */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {packs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-6">
            <p className="text-sm font-medium text-foreground">No sticker packs yet</p>
            <p className="text-xs mt-1 max-w-[240px] leading-relaxed">
              Tap “Add sticker pack” above and paste a `t.me/addstickers/...`
              link or just the pack name.
            </p>
          </div>
        ) : (
          packs.map((pack) => (
            <PackBlock
              key={pack.id}
              pack={pack}
              onSelectSticker={onSelect}
              onUninstall={() => handleUninstall(pack.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function PackBlock({
  pack,
  onSelectSticker,
  onUninstall,
}: {
  pack: StickerPackDTO
  onSelectSticker: (s: StickerDTO) => void
  onUninstall: () => void
}) {
  return (
    <div className="mb-4 first:mt-1">
      <div className="flex items-center justify-between px-2 mb-1">
        <div className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground truncate">
          {pack.title}{' '}
          {pack.scope === 'SERVER' && (
            <span className="ml-1 text-[9px] uppercase text-primary/70">server</span>
          )}
        </div>
        <button
          onClick={onUninstall}
          className="text-[11px] text-muted-foreground hover:text-destructive"
        >
          Remove
        </button>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {pack.stickers.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelectSticker(s)}
            title={s.emoji || ''}
            className="aspect-square flex items-center justify-center rounded hover:bg-accent transition p-1"
          >
            <StickerView sticker={s} size={64} />
          </button>
        ))}
      </div>
    </div>
  )
}
